import { get } from 'lodash-es';
import baseFavicon from 'assets/images/brands/teko/teko_favicon.png';
import baseLogo from 'assets/images/brands/teko/teko_logo.png';
import { appBrandConstants, outsidePathConstants } from 'constants/index';
import userHelpers from 'helpers/user';
import { IPermission, IRoute, UpgradeTimeRange } from 'interfaces';
import userServices from 'services/apis/user';
import configHelpers from 'helpers/config';
import { parseJsonObject } from './independent';

const { getClientConfig } = configHelpers;
const { appName: clientAppName, appLogo, appFavicon } = getClientConfig();
const { BRAND_CONFIGS } = appBrandConstants;
const { getCurrentSeller } = userHelpers;
const { getUserInfo } = userServices;

/**
 * Builds a permission object from 2 or 3 arguments
 * @param app
 * @param resource
 * @param action optional
 */
export const permission = (
  app: IPermission['app'],
  resource: IPermission['resource'],
  action?: IPermission['action']
): IPermission => ({ app, resource, action });

const getWindowDimensions = () => {
  return {
    width: window.innerWidth,
    height: window.innerHeight,
  };
};

const getBrandConfig = () => {
  const appName =
    clientAppName ||
    get(BRAND_CONFIGS, [window.location.host, 'appName'], 'Staff Desktop');
  const logo =
    appLogo || get(BRAND_CONFIGS, [window.location.host, 'logo'], baseLogo);
  const favicon =
    appFavicon ||
    get(BRAND_CONFIGS, [window.location.host, 'favicon'], baseFavicon);
  return { appName, logo, favicon };
};

const getExternalPath = (route: IRoute) => {
  let routePath = route.path;
  if (route.needAppendSellerId) {
    routePath += '&seller_id=' + getCurrentSeller();
  }
  if (route.needAppendUserId) {
    const { sub } = getUserInfo();
    routePath += '&user_id=' + sub;
  }
  return routePath;
};

const getUpgradeTimeRange = (
  upgradeTimeRange?: UpgradeTimeRange,
  currentRoute?: IRoute
) => {
  if (!upgradeTimeRange || !currentRoute) {
    return undefined;
  }

  // Check if any key of upgradeTimeRange is matched with current host (math by startWith)
  const matchedUpgradeTimeRangeKey = Object.keys(upgradeTimeRange).find(key =>
    currentRoute.iFrameSrc?.startsWith(key)
  );

  if (!matchedUpgradeTimeRangeKey) {
    return undefined;
  }

  return {
    upgradeStartTime: new Date(
      upgradeTimeRange[matchedUpgradeTimeRangeKey].from
    ),
    upgradeEndTime: new Date(upgradeTimeRange[matchedUpgradeTimeRangeKey].to),
    serviceName: upgradeTimeRange[matchedUpgradeTimeRangeKey]?.service,
  };
};

const noAccentVietnamese = (data: string) => {
  if (!data) return data;

  data = data.replace(/[àáạảãâầấậẩẫăằắặẳẵ]/g, 'a');
  data = data.replace(/[ÀÁẠẢÃĂẰẮẶẲẴÂẦẤẬẨẪ]/g, 'A');
  data = data.replace(/[èéẹẻẽêềếệểễ]/g, 'e');
  data = data.replace(/[ÈÉẸẺẼÊỀẾỆỂỄ]/g, 'E');
  data = data.replace(/[òóọỏõôồốộổỗơờớợởỡ]/g, 'o');
  data = data.replace(/[ÒÓỌỎÕÔỒỐỘỔỖƠỜỚỢỞỠ]/g, 'O');
  data = data.replace(/[ìíịỉĩ]/g, 'i');
  data = data.replace(/[ÌÍỊỈĨ]/g, 'I');
  data = data.replace(/[ùúụủũưừứựửữ]/g, 'u');
  data = data.replace(/[ƯỪỨỰỬỮÙÚỤỦŨ]/g, 'U');
  data = data.replace(/[ỳýỵỷỹ]/g, 'y');
  data = data.replace(/[ỲÝỴỶỸ]/g, 'Y');
  data = data.replace(/[đ]/g, 'd');
  data = data.replace(/[Đ]/g, 'D');

  return data;
};

/**
 * return true if route.path in children
 *
 * return true if children array contains relative path for case isCustomWebsiteItem
 * Ex: children = ['https://base-domain/:websiteId'], route.path = 'https://base-domain/123' => return true
 */
const checkShowMenuItem = (children: string[], route: IRoute) => {
  const {
    websiteEditorPathRegex,
    CUSTOM_WEBSITE_UI_PATH,
    CUSTOM_MOBILE_APP_UI_PATH,
    appEditorPathRegex,
  } = outsidePathConstants;

  return (
    children.includes(route.path) ||
    (children.includes(CUSTOM_WEBSITE_UI_PATH) &&
      route.isCustomWebsiteItem &&
      websiteEditorPathRegex.test(route.path)) ||
    (children.includes(CUSTOM_MOBILE_APP_UI_PATH) &&
      route.isCustomWebsiteItem &&
      appEditorPathRegex.test(route.path))
  );
};

export default {
  permission,
  getWindowDimensions,
  getBrandConfig,
  getExternalPath,
  getUpgradeTimeRange,
  parseJsonObject,
  noAccentVietnamese,
  checkShowMenuItem,
};
